import { createContext, useContext } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'

import usePatientQuery from '@shared/hooks/src/usePatient'
import API from '@shared/services/src/API'
import { QK } from '@shared/utils'

export const PatientContext = createContext(undefined)

/**
 * Provide a context for detailed patient information.
 * This context can be accessed in children via usePatient hook
 *
 * @param {string} id - The patient id
 * @param {React.ReactNode} children - The children to render
 */
export function PatientProvider({ id, children }) {
  const { data } = usePatientQuery(id, { enabled: Boolean(id) })

  return <PatientContext.Provider value={data}>{children}</PatientContext.Provider>
}

export function PatientSuspenseProvider({ id, children }) {
  const { data } = useSuspenseQuery({
    queryKey: QK.patients.id(id).details,
    queryFn: () => API.patients.id(id).details(),
  })

  return <PatientContext.Provider value={data}>{children}</PatientContext.Provider>
}

export const usePatient = () => useContext(PatientContext)

// HOC version of usePatient
export const withPatient = (Component) => (props) => (
  <PatientContext.Consumer>{(patient) => <Component {...props} patient={patient} />}</PatientContext.Consumer>
)
